<template>
  <ui-dialog :show="isOpen" max-width="623" @input="handleClose">
    <template #header>
      <h3 v-if="status">
        {{ $t(`message.posts.list.facilitiesDialog.${status}.title`) }}
      </h3>
      <h3 v-else>
        {{ $t(`message.posts.list.facilitiesDialog.defaultTitle`) }}
      </h3>
    </template>

    <template #default>
      <v-text-field
        v-model="postsSearchInputValue"
        :placeholder="
          $t(
            'message.businessAddresses.groups.facilities.filterInputPlaceholder'
          )
        "
        prepend-inner-icon="icon-search"
        clearable
        outlined
        rounded
        class="mb-4"
        :hide-details="true"
        @input="onFacilitiesSearch"
      ></v-text-field>
      <v-row no-gutters class="align-center mb-3">
        <v-col>
          <Pagination
            class="flex-grow-1"
            :page="page"
            :per-page="perPage"
            :options="perPageOptions"
            :total-count="totalCount"
            :disabled="loading"
            @change="handleChange"
          >
            <div class="post-facilities-dialog__positions-count">
              {{ $t("message.common.positionsCount", [totalCount]) }}
            </div>
          </Pagination>
        </v-col>
      </v-row>

      <div v-if="loaded" class="position-relative">
        <v-virtual-scroll
          class="list"
          :items="facilities"
          :item-height="listItemHeight"
          :height="listHeight"
        >
          <template v-slot:default="{ item }">
            <v-list-item :key="item.id" :ripple="false" class="list-item">
              <v-list-item-content class="d-flex flex-col">
                <div class="d-flex align-center">
                  <IsOwnerIndicator :isOwner="true" class="mr-3" />
                  <div class="list-item__content-primary">
                    <p class="list-item__name">{{ item.name }}</p>
                    <div class="list-item__description">
                      <p class="list-item__address">
                        {{ item.formatted_address }}
                      </p>
                    </div>
                  </div>
                </div>
                <div v-if="item.error" class="list-item__error">
                  <v-icon color="pink" size="18">
                    mdi-information
                  </v-icon>
                  <div class="d-flex flex-column ml-2">
                    <p>{{ item.error.message }}</p>
                    <a class="contact-link" :href="`mailto:${supportEmail}`">
                      {{
                        $t(
                          "message.posts.list.facilitiesDialog.fixError.general"
                        )
                      }}
                    </a>
                  </div>
                </div>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-virtual-scroll>

        <SectionLoader :status="loading" :opacity="0.75" />
      </div>
      <div v-else class="no-data">
        <SectionLoader :status="loading" :opacity="0.75" />
      </div>
    </template>

    <template #actions>
      <div class="w-100 d-flex justify-center" v-if="isFailedDialog">
        <!-- <ui-button color="primary" outlined @click="handleClose" class="mx-2">
          {{ $t("message.common.cancel") }}
        </ui-button>
        <ui-button color="primary" class="mx-2" @click="handleResend">
          {{ $t("message.common.resend") }}
        </ui-button> -->
      </div>

      <v-row v-else no-gutters class="bottom-pagination">
        <v-col>
          <Pagination
            class="flex-grow-1"
            :page="page"
            :per-page="perPage"
            :show-options="false"
            :options="perPageOptions"
            :total-count="totalCount"
            :disabled="loading"
            @change="handleChange"
          />
        </v-col>
      </v-row>
    </template>
  </ui-dialog>
</template>

<script>
import Pagination from "@/components/List/Pagination";
import SectionLoader from "@/components/Loader/SectionLoader";
import IsOwnerIndicator from "@/components/Permissions/IsOwnerIndicator.vue";

import { debounce } from "@/helpers/helpers";
import postsService from "@/services/gmbPosts";
import { POST_BY_FACILITY_STATUS } from "@/enums/posts.enum";
import constants from "@/config/constants";

export default {
  name: "PostFacilitiesDialog",
  components: { Pagination, SectionLoader, IsOwnerIndicator },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    postId: {
      type: Number
    },
    mediaId: {
      type: Number
    },
    fetchMethod: {
      type: Function,
      default: postsService.getPostFacilities
    },
    status: {
      type: Number,
      validate: value => Object.values(POST_BY_FACILITY_STATUS).includes(value)
    }
  },
  data() {
    return {
      loaded: false,
      loading: false,
      postsSearchInputValue: "",
      totalCount: 0,
      page: 1,
      perPageOptions: [10, 25, 50, 100],
      perPage: 10,
      facilities: [],
      supportEmail: constants.centerAiEmail
    };
  },
  watch: {
    async isOpen(newVal) {
      if (newVal) {
        this.loading = true;
        await this.fetchFacilities();
        this.loading = false;
        this.loaded = true;
      } else {
        this.loaded = false;
        this.postsSearchInputValue = "";
        this.page = 1;
        this.perPage = 10;
        this.totalCount = 0;
        this.facilities = [];
      }
    }
  },
  computed: {
    isFailedDialog() {
      return this.status && this.status === POST_BY_FACILITY_STATUS.FAILED;
    },
    listItemHeight() {
      return this.isFailedDialog ? 123 : 65;
    },
    listHeight() {
      const maxListItems = this.isFailedDialog ? 4 : 8;

      if (this.facilities.length > maxListItems) {
        return this.listItemHeight * maxListItems;
      } else if (this.facilities.length < 2) {
        return this.listItemHeight * 2;
      } else {
        return this.listItemHeight * this.facilities.length;
      }
    }
  },
  methods: {
    async fetchFacilities() {
      const params = {
        ...(this.postId && { postId: this.postId }),
        ...(this.mediaId && { mediaId: this.mediaId }),
        page: this.page,
        perPage: this.perPage,
        statuses: [this.status],
        query: this.postsSearchInputValue
      };

      const response = await this.fetchMethod(params);

      if (
        response &&
        response.data &&
        "results" in response.data &&
        "count" in response.data
      ) {
        this.facilities = [...response.data.results];
        this.totalCount = response.data.count;
      }
    },

    onFacilitiesSearch: debounce(async function() {
      this.loading = true;
      this.page = 1;
      await this.fetchFacilities();
      this.loading = false;
    }, 500),

    async handleChange({ page, perPage }) {
      this.loading = true;
      this.page = page;
      this.perPage = perPage;
      await this.fetchFacilities();
      this.loading = false;
    },

    handleClose() {
      this.$emit("close");
    },

    handleResend() {
      // TODO
    }
  }
};
</script>

<style lang="scss" scoped>
.list {
  border-top: 1px solid $light-3;
  position: relative;
}

.list-item {
  border-bottom: 1px solid $light-3;

  &__name,
  &__description {
    color: $dark;
  }

  &__name {
    @include font-size(14px);
    color: $text;
    font-weight: 500;
    margin-top: $spacing * 0.5;
    margin-bottom: 0;
  }

  &__description {
    position: relative;
    display: flex;
    @include font-size(12px);
    font-weight: 400;
    margin-bottom: 0;
    max-width: 100%;
    white-space: nowrap;
    color: $text;

    & > p {
      margin-bottom: 0;
    }
  }

  &__address {
    margin-right: $spacing;
    position: relative;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__error {
    display: flex;
    align-items: flex-start;
    margin-top: $spacing;
    margin-bottom: 0;
    margin-left: $spacing * 4;
    padding: $spacing $spacing * 2;
    border-radius: $spacing * 0.5;
    background-color: $light-4;
    color: $text;
    font-weight: 400;
    @include font-size(12px);

    p {
      margin-bottom: 0;
      margin-top: $spacing * 0.25;
    }
  }
}

.contact-link {
  margin-top: $spacing * 0.5;
  color: $pink;
  font-weight: 500;
}

.bottom-pagination {
  margin-bottom: $spacing * 1.5;
  position: relative;
}

.no-data {
  @include noData(350px);
}
</style>

<style lang="scss">
.post-facilities-dialog__positions-count {
  color: $text;
  font-weight: 400;
  @include font-size(12px);
}
</style>
